/**
 * AdminAnimationStudioTab — admin UI for managing the render-time
 * animation that plays while a song generates.
 *
 * Replaces the legacy hard-coded ChameleonStage. Admin can:
 *   • Manage Rooms (locations like Lyrics Room, Studio, Lobby...)
 *   • Upload + organise 10s MP4 clips into rooms
 *   • Build Batches (ordered playlists) and tag them by genre
 *   • Edit duration tunables
 *
 * Layout is a top-level 4-sub-tab switcher inside the parent admin
 * "Animation Studio" tab — Rooms / Clips Library / Batches /
 * Settings — to keep each panel focused.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2, Plus, Trash2, Upload, Save, X, Film, DoorOpen,
  ListOrdered, Settings as SettingsIcon, Search, Volume2, VolumeX,
  GripVertical, Check, RefreshCw, Play, FastForward,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../App';

const SUB_TABS = [
  { id: 'rooms',    label: 'Rooms',         icon: DoorOpen },
  { id: 'clips',    label: 'Clips Library', icon: Film },
  { id: 'batches',  label: 'Batches',       icon: ListOrdered },
  { id: 'settings', label: 'Settings',      icon: SettingsIcon },
];

export default function AdminAnimationStudioTab() {
  const [activeSub, setActiveSub] = useState('rooms');

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 border-b border-white/10 pb-2 flex-wrap">
        {SUB_TABS.map((t) => {
          const Icon = t.icon;
          const active = activeSub === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveSub(t.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition ${
                active
                  ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                  : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white border border-transparent'
              }`}
              data-testid={`anim-subtab-${t.id}`}
            >
              <Icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {activeSub === 'rooms' && <RoomsPanel />}
      {activeSub === 'clips' && <ClipsPanel />}
      {activeSub === 'batches' && <BatchesPanel />}
      {activeSub === 'settings' && <SettingsPanel />}
    </div>
  );
}

// ============================================================================
// ROOMS PANEL
// ============================================================================
function RoomsPanel() {
  const [rooms, setRooms] = useState([]);
  const [firstLoad, setFirstLoad] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');

  // `load` does NOT flip a loading flag after the first call — doing
  // so would unmount every RoomRow during a silent refresh and wipe
  // the unsaved edits the user made in OTHER rows (the "saved row 1,
  // lost rows 2-6" bug). We just swap the data in place and let React
  // diff the props on each row; rows that weren't keyed-out keep
  // their local state.
  const load = useCallback(async () => {
    try {
      const { data } = await apiFetch('/admin/animation/rooms');
      setRooms(data.rooms || []);
    } catch (e) {
      toast.error(e?.message || 'Failed to load rooms');
    } finally {
      setFirstLoad(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!newName.trim()) return toast.error('Room name required');
    setCreating(true);
    try {
      await apiFetch('/admin/animation/rooms', {
        method: 'POST',
        body: JSON.stringify({ name: newName, description: newDescription }),
      });
      toast.success('Room created');
      setNewName('');
      setNewDescription('');
      load();
    } catch (e) {
      toast.error(e?.message || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const update = async (roomId, patch) => {
    try {
      await apiFetch(`/admin/animation/rooms/${roomId}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
      toast.success('Room saved');
      load();
    } catch (e) {
      toast.error(e?.message || 'Update failed');
    }
  };

  const remove = async (room) => {
    if (!window.confirm(`Delete room "${room.name}"? This won't delete clips.`)) return;
    try {
      await apiFetch(`/admin/animation/rooms/${room.room_id}`, { method: 'DELETE' });
      toast.success('Room deleted');
      load();
    } catch (e) {
      toast.error(e?.message || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white/[0.03] border border-white/10 rounded-lg p-4">
        <h3 className="text-white font-medium mb-3 flex items-center gap-2">
          <Plus className="h-4 w-4" /> Add New Room
        </h3>
        <div className="flex gap-2 flex-wrap">
          <input
            type="text"
            placeholder="Room name (e.g. Lyrics Room)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="flex-1 min-w-[200px] bg-black/30 text-white text-sm px-3 py-2 rounded border border-white/10 outline-none focus:border-amber-500"
            data-testid="anim-room-new-name"
          />
          <input
            type="text"
            placeholder="Description (optional)"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            className="flex-[2] min-w-[200px] bg-black/30 text-white text-sm px-3 py-2 rounded border border-white/10 outline-none focus:border-amber-500"
            data-testid="anim-room-new-desc"
          />
          <button
            type="button"
            onClick={create}
            disabled={creating || !newName.trim()}
            className="px-3 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40 rounded text-sm flex items-center gap-1 disabled:opacity-40"
            data-testid="anim-room-create-btn"
          >
            {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Create
          </button>
        </div>
      </div>

      {firstLoad ? (
        <div className="text-center py-8 text-zinc-500">Loading rooms…</div>
      ) : (
        <div className="space-y-2">
          {rooms.map((r) => (
            <RoomRow key={r.room_id} room={r} onSave={update} onDelete={remove} />
          ))}
          {rooms.length === 0 && (
            <div className="text-center py-8 text-zinc-500 text-sm">
              No rooms yet — create your first room above.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RoomRow({ room, onSave, onDelete }) {
  const [name, setName] = useState(room.name);
  const [desc, setDesc] = useState(room.description || '');
  const [order, setOrder] = useState(room.display_order ?? room.room_number);
  const [enabled, setEnabled] = useState(room.enabled !== false);
  const dirty = name !== room.name || desc !== (room.description || '') ||
    order !== (room.display_order ?? room.room_number) || enabled !== (room.enabled !== false);

  return (
    <div
      className={`bg-white/[0.03] border border-white/10 rounded-lg p-3 flex items-center gap-3 flex-wrap ${
        enabled ? '' : 'opacity-50'
      }`}
      data-testid={`anim-room-row-${room.room_id}`}
    >
      <div className="text-amber-400 font-mono text-sm font-bold w-12">R{room.room_number}</div>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="bg-black/30 text-white text-sm px-2 py-1 rounded border border-white/10 outline-none w-44"
      />
      <input
        type="text"
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        placeholder="Description"
        className="flex-1 min-w-[160px] bg-black/30 text-zinc-300 text-xs px-2 py-1 rounded border border-white/10 outline-none"
      />
      <label className="flex items-center gap-1 text-xs text-zinc-400">
        Order
        <input
          type="number"
          value={order}
          onChange={(e) => setOrder(Number(e.target.value))}
          className="w-14 bg-black/30 text-white text-xs px-1.5 py-1 rounded border border-white/10 outline-none"
        />
      </label>
      <label className="flex items-center gap-1 text-xs text-zinc-400">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        Enabled
      </label>
      <button
        type="button"
        onClick={() => onSave(room.room_id, { name, description: desc, display_order: order, enabled })}
        disabled={!dirty}
        className="px-2 py-1 text-xs rounded bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-500/40 disabled:opacity-30"
        data-testid={`anim-room-save-${room.room_id}`}
      >
        <Save className="h-3 w-3" />
      </button>
      <button
        type="button"
        onClick={() => onDelete(room)}
        className="px-2 py-1 text-xs rounded bg-rose-500/20 hover:bg-rose-500/30 text-rose-200 border border-rose-500/40"
        data-testid={`anim-room-delete-${room.room_id}`}
      >
        <Trash2 className="h-3 w-3" />
      </button>
    </div>
  );
}

// ============================================================================
// CLIPS PANEL
// ============================================================================
function ClipsPanel() {
  const [rooms, setRooms] = useState([]);
  const [clips, setClips] = useState([]);
  const [firstLoad, setFirstLoad] = useState(true);
  const [filterRoomId, setFilterRoomId] = useState('');
  const [search, setSearch] = useState('');
  const [uploadingRoom, setUploadingRoom] = useState(null);
  const fileInputRef = useRef(null);
  // Enforce single-clip playback across ALL cards in the Clips Library so
  // hitting Play on one auto-pauses whichever clip was previously playing.
  // Avoids overlapping audio when the admin is auditing multiple clips.
  const activeVideoRef = useRef(null);
  const stopOtherVideos = useCallback((current) => {
    const prev = activeVideoRef.current;
    if (prev && prev !== current && !prev.paused) {
      try {
        prev.pause();
      } catch { /* ignore */ }
    }
    activeVideoRef.current = current;
  }, []);

  // See RoomsPanel for why we don't flip `loading` after first load.
  const load = useCallback(async () => {
    try {
      const [r, c] = await Promise.all([
        apiFetch('/admin/animation/rooms'),
        apiFetch(`/admin/animation/clips${filterRoomId ? `?room_id=${filterRoomId}` : ''}`),
      ]);
      setRooms(r.data.rooms || []);
      setClips(c.data.clips || []);
    } catch (e) {
      toast.error(e?.message || 'Load failed');
    } finally {
      setFirstLoad(false);
    }
  }, [filterRoomId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clips;
    return clips.filter(
      (c) =>
        c.clip_id.toLowerCase().includes(q) ||
        (c.label || '').toLowerCase().includes(q),
    );
  }, [clips, search]);

  const nextClipNumberFor = (roomId) => {
    const existing = clips.filter((c) => c.room_id === roomId).map((c) => c.clip_number);
    return existing.length === 0 ? 1 : Math.max(...existing) + 1;
  };

  const onUploadClick = (room) => {
    setUploadingRoom(room);
    fileInputRef.current?.click();
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so same file can be re-chosen
    if (!file || !uploadingRoom) return;
    const clipNumber = nextClipNumberFor(uploadingRoom.room_id);
    const fd = new FormData();
    fd.append('room_id', uploadingRoom.room_id);
    fd.append('clip_number', String(clipNumber));
    fd.append('label', file.name.replace(/\.[^.]+$/, '').slice(0, 60));
    fd.append('audio_enabled', 'true');
    fd.append('file', file);
    const tid = toast.loading(`Uploading ${file.name} as Room${uploadingRoom.room_number}C${String(clipNumber).padStart(3, '0')}…`);
    try {
      await apiFetch('/admin/animation/clips/upload', { method: 'POST', body: fd });
      toast.dismiss(tid);
      toast.success(`Uploaded → Room${uploadingRoom.room_number}C${String(clipNumber).padStart(3, '0')}`);
      load();
    } catch (err) {
      toast.dismiss(tid);
      toast.error(err?.message || 'Upload failed');
    } finally {
      setUploadingRoom(null);
    }
  };

  const updateClip = async (clipId, patch) => {
    try {
      await apiFetch(`/admin/animation/clips/${clipId}`, { method: 'PATCH', body: JSON.stringify(patch) });
      toast.success('Clip saved');
      load();
    } catch (e) { toast.error(e?.message || 'Update failed'); }
  };

  const deleteClip = async (clip) => {
    if (!window.confirm(`Delete clip ${clip.clip_id}? This will also remove it from any batch that references it.`)) return;
    try {
      await apiFetch(`/admin/animation/clips/${clip.clip_id}`, { method: 'DELETE' });
      toast.success(`Deleted ${clip.clip_id}`);
      load();
    } catch (e) { toast.error(e?.message || 'Delete failed'); }
  };

  return (
    <div className="space-y-4">
      {/* hidden picker — drives all uploads via the per-room buttons below */}
      <input
        ref={fileInputRef}
        type="file"
        accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
        onChange={onFileChosen}
        style={{ display: 'none' }}
        data-testid="anim-clip-file-input"
      />

      {/* Upload buttons grid — one per room */}
      <div className="bg-white/[0.03] border border-white/10 rounded-lg p-4">
        <h3 className="text-white font-medium mb-3 flex items-center gap-2">
          <Upload className="h-4 w-4" /> Upload Clip to a Room
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
          {rooms.filter((r) => r.enabled !== false).map((r) => {
            const next = nextClipNumberFor(r.room_id);
            return (
              <button
                key={r.room_id}
                type="button"
                onClick={() => onUploadClick(r)}
                className="text-left bg-white/5 hover:bg-amber-500/15 border border-white/10 hover:border-amber-500/40 rounded p-2 transition group"
                data-testid={`anim-clip-upload-to-${r.room_id}`}
              >
                <div className="text-xs text-amber-400 font-mono mb-0.5">R{r.room_number}</div>
                <div className="text-sm text-white truncate">{r.name}</div>
                <div className="text-[10px] text-zinc-500 mt-0.5">Next: Room{r.room_number}C{String(next).padStart(3, '0')}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Filter + search */}
      <div className="flex gap-2 flex-wrap items-center">
        <select
          value={filterRoomId}
          onChange={(e) => setFilterRoomId(e.target.value)}
          className="bg-black/30 text-white text-sm px-2 py-1.5 rounded border border-white/10 outline-none"
          data-testid="anim-clip-room-filter"
        >
          <option value="">All rooms ({clips.length})</option>
          {rooms.map((r) => (
            <option key={r.room_id} value={r.room_id}>
              R{r.room_number} · {r.name}
            </option>
          ))}
        </select>
        <div className="relative flex-1 min-w-[180px]">
          <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            placeholder="Search clip_id or label…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-black/30 text-white text-sm pl-7 pr-2 py-1.5 rounded border border-white/10 outline-none"
            data-testid="anim-clip-search"
          />
        </div>
        <button onClick={load} className="text-xs text-zinc-400 hover:text-white flex items-center gap-1">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      {/* Clips grid */}
      {firstLoad ? (
        <div className="text-center py-8 text-zinc-500">Loading clips…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((c) => (
            <ClipCard
              key={c.clip_id}
              clip={c}
              room={rooms.find((r) => r.room_id === c.room_id)}
              onSave={updateClip}
              onDelete={deleteClip}
              stopOtherVideos={stopOtherVideos}
            />
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-8 text-zinc-500 text-sm">
              {clips.length === 0 ? 'No clips uploaded yet.' : 'No clips match your filter.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ClipCard({ clip, room, onSave, onDelete, stopOtherVideos }) {
  const [label, setLabel] = useState(clip.label || '');
  const [audio, setAudio] = useState(clip.audio_enabled !== false);
  const [enabled, setEnabled] = useState(clip.enabled !== false);
  const [autoDuration, setAutoDuration] = useState(clip.duration_sec);
  const [visible, setVisible] = useState(false);
  const cardRef = useRef(null);
  const dirty = label !== (clip.label || '') || audio !== (clip.audio_enabled !== false) || enabled !== (clip.enabled !== false);

  // IntersectionObserver — defer expensive video metadata loading until
  // the card actually scrolls into view. Without this, a 50-clip
  // library fires 50 simultaneous CDN requests and the browser's
  // per-host connection cap silently drops most of them, leaving
  // cards stuck at '?s'.
  useEffect(() => {
    const el = cardRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      // Fallback for very old browsers — just probe immediately.
      setVisible(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            obs.disconnect();
            break;
          }
        }
      },
      { rootMargin: '200px' }, // start loading slightly before the card is visible
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Browser-side duration probe: when the <video> below loads its
  // metadata, capture the real duration. If the clip currently has no
  // duration on file (ffprobe might not be available on every pod),
  // silently PATCH it back to the server. Cheap and runs once per
  // clip per session — gives accurate batch-total math without any
  // backend tooling dependency.
  const onMetadataLoaded = (e) => {
    const d = e?.target?.duration;
    if (!d || !Number.isFinite(d)) return;
    const rounded = Math.round(d * 100) / 100;
    setAutoDuration(rounded);
    if (!clip.duration_sec || Math.abs(clip.duration_sec - rounded) > 0.5) {
      // Persist silently — no toast (this is admin housekeeping).
      apiFetch(`/admin/animation/clips/${clip.clip_id}`, {
        method: 'PATCH',
        body: JSON.stringify({ duration_sec: rounded }),
      }).catch(() => { /* non-essential */ });
    }
  };

  return (
    <div ref={cardRef} className={`bg-white/[0.03] border border-white/10 rounded-lg p-3 ${enabled ? '' : 'opacity-50'}`} data-testid={`anim-clip-card-${clip.clip_id}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="font-mono text-xs text-amber-400">{clip.clip_id}</div>
          <div className="text-[10px] text-zinc-500 truncate">{room?.name || clip.room_id}</div>
        </div>
        <div className="text-[10px] text-zinc-400 shrink-0">
          {autoDuration ? `${autoDuration.toFixed(1)}s` : '?s'} · {clip.size_bytes ? `${(clip.size_bytes / 1048576).toFixed(1)}MB` : '?'}
        </div>
      </div>
      <video
        src={visible ? clip.video_url : undefined}
        controls
        preload="metadata"
        muted={!audio}
        onPlay={stopOtherVideos ? (e) => stopOtherVideos(e.currentTarget) : undefined}
        onLoadedMetadata={onMetadataLoaded}
        className="w-full aspect-video bg-black rounded mb-2"
        data-testid={`anim-clip-video-${clip.clip_id}`}
      />
      <input
        type="text"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Label / caption"
        className="w-full bg-black/30 text-white text-xs px-2 py-1 rounded border border-white/10 outline-none mb-2"
      />
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <label className="flex items-center gap-1 text-xs text-zinc-400">
          <input type="checkbox" checked={audio} onChange={(e) => setAudio(e.target.checked)} />
          {audio ? <Volume2 className="h-3 w-3" /> : <VolumeX className="h-3 w-3" />} Audio
        </label>
        <label className="flex items-center gap-1 text-xs text-zinc-400">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Enabled
        </label>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => onSave(clip.clip_id, { label, audio_enabled: audio, enabled })}
            disabled={!dirty}
            className="px-2 py-1 text-xs rounded bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-500/40 disabled:opacity-30"
            data-testid={`anim-clip-save-${clip.clip_id}`}
          >
            <Save className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(clip)}
            className="px-2 py-1 text-xs rounded bg-rose-500/20 hover:bg-rose-500/30 text-rose-200 border border-rose-500/40"
            data-testid={`anim-clip-delete-${clip.clip_id}`}
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// BATCHES PANEL
// ============================================================================
function BatchesPanel() {
  const [batches, setBatches] = useState([]);
  const [clips, setClips] = useState([]);
  const [firstLoad, setFirstLoad] = useState(true);
  const [newName, setNewName] = useState('');

  // See RoomsPanel for why we don't flip `loading` after first load.
  const load = useCallback(async () => {
    try {
      const [b, c] = await Promise.all([
        apiFetch('/admin/animation/batches'),
        apiFetch('/admin/animation/clips'),
      ]);
      setBatches(b.data.batches || []);
      setClips(c.data.clips || []);
    } catch (e) {
      toast.error(e?.message || 'Load failed');
    } finally {
      setFirstLoad(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!newName.trim()) return toast.error('Batch name required');
    try {
      await apiFetch('/admin/animation/batches', {
        method: 'POST',
        body: JSON.stringify({ name: newName, selection_mode: 'ordered' }),
      });
      toast.success('Batch created');
      setNewName('');
      load();
    } catch (e) { toast.error(e?.message || 'Create failed'); }
  };

  const update = async (batchId, patch) => {
    try {
      await apiFetch(`/admin/animation/batches/${batchId}`, { method: 'PATCH', body: JSON.stringify(patch) });
      toast.success('Batch saved');
      load();
    } catch (e) { toast.error(e?.message || 'Update failed'); }
  };

  const remove = async (batch) => {
    if (!window.confirm(`Delete batch "${batch.name}"?`)) return;
    try {
      await apiFetch(`/admin/animation/batches/${batch.batch_id}`, { method: 'DELETE' });
      toast.success('Batch deleted');
      load();
    } catch (e) { toast.error(e?.message || 'Delete failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white/[0.03] border border-white/10 rounded-lg p-4">
        <div className="flex gap-2 flex-wrap">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New batch name (e.g. Disco Floor Journey)"
            className="flex-1 min-w-[200px] bg-black/30 text-white text-sm px-3 py-2 rounded border border-white/10 outline-none"
            data-testid="anim-batch-new-name"
          />
          <button
            type="button"
            onClick={create}
            disabled={!newName.trim()}
            className="px-3 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40 rounded text-sm flex items-center gap-1 disabled:opacity-40"
            data-testid="anim-batch-create-btn"
          >
            <Plus className="h-3.5 w-3.5" /> Create Batch
          </button>
        </div>
      </div>

      {firstLoad ? (
        <div className="text-center py-8 text-zinc-500">Loading batches…</div>
      ) : (
        <div className="space-y-3">
          {batches.map((b) => (
            <BatchRow key={b.batch_id} batch={b} clips={clips} onSave={update} onDelete={remove} />
          ))}
          {batches.length === 0 && (
            <div className="text-center py-8 text-zinc-500 text-sm">
              No batches yet — create your first batch above. Upload some clips first if you haven&apos;t.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BatchRow({ batch, clips, onSave, onDelete }) {
  const [name, setName] = useState(batch.name);
  const [desc, setDesc] = useState(batch.description || '');
  const [mode, setMode] = useState(batch.selection_mode || 'ordered');
  const [genres, setGenres] = useState((batch.genre_tags || []).join(', '));
  const [isDefault, setIsDefault] = useState(!!batch.is_default);
  const [enabled, setEnabled] = useState(batch.enabled !== false);
  const [sequence, setSequence] = useState(batch.clip_sequence || []);
  const [outroClip, setOutroClip] = useState(batch.outro_clip || '');
  const [expanded, setExpanded] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  // Hover-preview popover for the "Available clips" chips. `hoverClip`
  // holds the clip + viewport coords; a fixed-position <video> renders
  // near the chip so admins can see the actual footage before appending.
  const [hoverClip, setHoverClip] = useState(null);
  const hoverTimerRef = useRef(null);

  const showHoverPreview = (clip, e) => {
    if (!clip?.video_url) return;
    // Clear any pending hide from the previous chip so quick sweeps between
    // chips don't flicker the popup off.
    if (hoverTimerRef.current) { clearTimeout(hoverTimerRef.current); hoverTimerRef.current = null; }
    // Centre-anchored preview: always render in the upper middle of the
    // viewport so admins get a consistent, non-flickering preview no
    // matter which chip they hover. Avoids the mouse-tracking dead-zone
    // that made the popup flicker off between chip and popup.
    const POPUP_W = 320;
    const POPUP_H = 320;
    const x = Math.max(8, Math.round((window.innerWidth - POPUP_W) / 2));
    // Sit in the upper third — clear of the chip strip but not glued to
    // the top edge, so it reads as a floating dialog.
    const y = Math.max(24, Math.round(window.innerHeight * 0.12));
    // Suppress lint for unused rect param — we still call getBoundingClientRect
    // for potential future features (e.g. arrow pointing at source chip).
    void e;
    setHoverClip({ clip, x, y });
  };
  const cancelHide = () => {
    if (hoverTimerRef.current) { clearTimeout(hoverTimerRef.current); hoverTimerRef.current = null; }
  };
  const hideHoverPreview = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    // Longer grace than the standard 80ms — video previews can take a
    // beat to start playing, and admins routinely move the mouse from
    // chip → popup to read the label + duration.
    hoverTimerRef.current = setTimeout(() => setHoverClip(null), 250);
  };

  const totalDuration = useMemo(() => {
    return sequence.reduce((sum, cid) => {
      const c = clips.find((cl) => cl.clip_id === cid);
      return sum + (c?.duration_sec || 10);
    }, 0);
  }, [sequence, clips]);

  const minutes = Math.floor(totalDuration / 60);
  const seconds = Math.round(totalDuration % 60);
  const durColor = totalDuration < 180 ? 'text-yellow-400' : totalDuration > 210 ? 'text-orange-400' : 'text-emerald-400';

  const save = () => onSave(batch.batch_id, {
    name,
    description: desc,
    selection_mode: mode,
    genre_tags: genres.split(',').map((t) => t.trim()).filter(Boolean),
    is_default: isDefault,
    enabled,
    clip_sequence: sequence,
    outro_clip: outroClip || null,
  });

  const addClip = (clipId) => {
    if (sequence.includes(clipId)) return;
    setSequence([...sequence, clipId]);
  };
  const removeClipAt = (idx) => setSequence(sequence.filter((_, i) => i !== idx));
  const moveClip = (idx, dir) => {
    const next = [...sequence];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setSequence(next);
  };

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-lg p-4" data-testid={`anim-batch-row-${batch.batch_id}`}>
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-transparent text-white font-medium text-base outline-none border-b border-transparent hover:border-white/20 focus:border-amber-500"
          />
          <input
            type="text"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="Description"
            className="w-full bg-transparent text-zinc-400 text-xs outline-none border-b border-transparent hover:border-white/10 focus:border-amber-500/50 mt-1"
          />
        </div>
        <div className="flex flex-col items-end gap-1 text-right">
          <div className={`font-mono text-sm font-bold ${durColor}`}>
            {minutes}:{String(seconds).padStart(2, '0')} <span className="text-[10px] text-zinc-500">/ 3:00–3:30</span>
          </div>
          <div className="text-[10px] text-zinc-500">{sequence.length} clip{sequence.length === 1 ? '' : 's'}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <select value={mode} onChange={(e) => setMode(e.target.value)} className="bg-black/30 text-white text-xs px-2 py-1 rounded border border-white/10">
          <option value="ordered">Mode: Ordered</option>
          <option value="shuffle">Mode: Shuffle</option>
          <option value="intro_shuffle_outro">Mode: Intro/Shuffle/Outro</option>
        </select>
        <input
          type="text"
          value={genres}
          onChange={(e) => setGenres(e.target.value)}
          placeholder="genre tags (pop, rock…)"
          className="bg-black/30 text-white text-xs px-2 py-1 rounded border border-white/10 outline-none"
        />
        <label className="flex items-center gap-1 text-xs text-zinc-400">
          <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
          Default fallback
        </label>
        <label className="flex items-center gap-1 text-xs text-zinc-400">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Enabled
        </label>
      </div>

      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        className="text-xs text-amber-400 hover:text-amber-300 mb-2"
      >
        {expanded ? '▼ Hide clip sequence builder' : '▶ Show clip sequence builder'}
      </button>

      {expanded && (
        <div className="space-y-3 border-t border-white/5 pt-3">
          {/* Current sequence */}
          <div>
            <div className="text-xs text-zinc-400 mb-1">Sequence ({sequence.length}) <span className="text-zinc-500">· hover a row to preview</span>:</div>
            {sequence.length === 0 ? (
              <div className="text-xs text-zinc-500 italic">Empty — click clips below to add them in order.</div>
            ) : (
              <div className="space-y-1">
                {sequence.map((cid, idx) => {
                  const c = clips.find((cl) => cl.clip_id === cid);
                  return (
                    <div
                      key={`${cid}-${idx}`}
                      className="flex items-center gap-2 bg-black/30 hover:bg-amber-500/10 px-2 py-1 rounded text-xs transition-colors"
                      onMouseEnter={c?.video_url ? (e) => showHoverPreview(c, e) : undefined}
                      onMouseLeave={c?.video_url ? hideHoverPreview : undefined}
                      data-testid={`anim-batch-seq-row-${cid}-${idx}`}
                    >
                      <GripVertical className="h-3 w-3 text-zinc-600" />
                      <span className="text-zinc-500 w-6 text-right">{idx + 1}.</span>
                      <span className="font-mono text-amber-400 w-24">{cid}</span>
                      <span className="text-zinc-300 truncate flex-1">{c?.label || '(missing)'}</span>
                      <span className="text-zinc-500">{c?.duration_sec ? `${c.duration_sec.toFixed(1)}s` : ''}</span>
                      <button onClick={() => moveClip(idx, -1)} className="text-zinc-400 hover:text-white px-1" disabled={idx === 0}>↑</button>
                      <button onClick={() => moveClip(idx, 1)} className="text-zinc-400 hover:text-white px-1" disabled={idx === sequence.length - 1}>↓</button>
                      <button onClick={() => removeClipAt(idx)} className="text-rose-400 hover:text-rose-300 px-1"><X className="h-3 w-3" /></button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Available clips (not yet added) */}
          <div>
            <div className="text-xs text-zinc-400 mb-1">Available clips (click to append · hover to preview):</div>
            <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto">
              {clips
                .filter((c) => c.enabled !== false && !sequence.includes(c.clip_id))
                .map((c) => (
                  <button
                    key={c.clip_id}
                    type="button"
                    onClick={() => addClip(c.clip_id)}
                    onMouseEnter={(e) => showHoverPreview(c, e)}
                    onMouseLeave={hideHoverPreview}
                    onFocus={(e) => showHoverPreview(c, e)}
                    onBlur={hideHoverPreview}
                    className="text-[10px] px-2 py-1 rounded bg-white/5 hover:bg-amber-500/20 text-zinc-300 hover:text-amber-200 border border-white/10 font-mono"
                    title={c.label}
                    data-testid={`anim-batch-add-clip-${c.clip_id}`}
                  >
                    + {c.clip_id}
                  </button>
                ))}
              {clips.length === 0 && <div className="text-xs text-zinc-500">Upload clips first.</div>}
            </div>
          </div>

          {/* Outro clip picker */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-zinc-400">Outro clip (plays when song finishes early):</span>
            <select
              value={outroClip}
              onChange={(e) => setOutroClip(e.target.value)}
              className="bg-black/30 text-white text-xs px-2 py-1 rounded border border-white/10"
              data-testid={`anim-batch-outro-${batch.batch_id}`}
            >
              <option value="">(none)</option>
              {clips.filter((c) => c.enabled !== false).map((c) => (
                <option key={c.clip_id} value={c.clip_id}>{c.clip_id} — {c.label || '(no label)'}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 mt-3">
        <button
          type="button"
          onClick={() => setPreviewing(true)}
          disabled={sequence.length === 0 && !outroClip}
          className="px-3 py-1.5 text-xs rounded bg-sky-500/20 hover:bg-sky-500/30 text-sky-200 border border-sky-500/40 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`anim-batch-preview-${batch.batch_id}`}
          title={sequence.length === 0 && !outroClip ? 'Add at least one clip first' : 'Preview the sequence in-browser'}
        >
          <Play className="h-3 w-3" /> Preview
        </button>
        <button
          type="button"
          onClick={save}
          className="px-3 py-1.5 text-xs rounded bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-500/40 flex items-center gap-1"
          data-testid={`anim-batch-save-${batch.batch_id}`}
        >
          <Check className="h-3 w-3" /> Save
        </button>
        <button
          type="button"
          onClick={() => onDelete(batch)}
          className="px-3 py-1.5 text-xs rounded bg-rose-500/20 hover:bg-rose-500/30 text-rose-200 border border-rose-500/40 flex items-center gap-1"
          data-testid={`anim-batch-delete-${batch.batch_id}`}
        >
          <Trash2 className="h-3 w-3" /> Delete
        </button>
      </div>

      {previewing && (
        <BatchPreviewModal
          batchName={name}
          clips={clips}
          sequence={sequence}
          outroClipId={outroClip}
          onClose={() => setPreviewing(false)}
        />
      )}

      {/* Hover preview popover for "Available clips" chips. */}
      {hoverClip && (
        <HoverClipPreview
          x={hoverClip.x}
          y={hoverClip.y}
          clip={hoverClip.clip}
        />
      )}
    </div>
  );
}

// ============================================================================
// BATCH PREVIEW MODAL — in-browser playback of a batch's clip sequence so
// admins can verify the visual flow + outro before saving / before a song
// generates. Resolves clip_id → clip doc on the fly so it reflects the
// admin's CURRENT (unsaved) edits.
// ============================================================================
function BatchPreviewModal({ batchName, clips, sequence, outroClipId, onClose }) {
  const clipLookup = useMemo(() => {
    const m = {};
    for (const c of clips) m[c.clip_id] = c;
    return m;
  }, [clips]);

  // Build the resolved playlist. Missing or disabled clips are skipped.
  const playlist = useMemo(() => {
    const out = [];
    for (const cid of sequence) {
      const c = clipLookup[cid];
      if (c && c.video_url && c.enabled !== false) {
        out.push({ ...c, isOutro: false });
      }
    }
    if (outroClipId) {
      const o = clipLookup[outroClipId];
      if (o && o.video_url && o.enabled !== false) {
        out.push({ ...o, isOutro: true });
      }
    }
    return out;
  }, [sequence, outroClipId, clipLookup]);

  const [index, setIndex] = useState(0);
  const [rate, setRate] = useState(1);
  const [muted, setMuted] = useState(false);
  const [done, setDone] = useState(false);
  const videoRef = useRef(null);

  // Apply the chosen rate whenever it changes or a new clip starts.
  useEffect(() => {
    const v = videoRef.current;
    if (v) v.playbackRate = rate;
  }, [rate, index]);

  const current = playlist[index];

  const onEnded = () => {
    if (index < playlist.length - 1) {
      setIndex((i) => i + 1);
    } else {
      setDone(true);
    }
  };

  const restart = () => {
    setIndex(0);
    setDone(false);
  };

  const totalDur = playlist.reduce((s, c) => s + (c.duration_sec || 10), 0);
  const totalMin = Math.floor(totalDur / 60);
  const totalSec = Math.round(totalDur % 60);

  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="anim-batch-preview-modal"
    >
      <div
        className="relative w-full max-w-3xl bg-zinc-900 border border-white/10 rounded-xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-white/10 bg-black/30">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white truncate">{batchName} — Preview</div>
            <div className="text-[11px] text-zinc-400">
              {playlist.length} clip{playlist.length === 1 ? '' : 's'} · ~{totalMin}:{String(totalSec).padStart(2, '0')} total
              {outroClipId && playlist.length > 0 && playlist[playlist.length - 1]?.isOutro && ' · ends with Outro'}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded hover:bg-white/10 text-zinc-300 hover:text-white"
            data-testid="anim-batch-preview-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Video / empty state */}
        <div className="relative aspect-video bg-black">
          {playlist.length === 0 ? (
            <div className="absolute inset-0 flex items-center justify-center text-zinc-400 text-sm text-center px-6">
              No playable clips in this batch yet — add some clips (or fix missing/disabled ones) and try again.
            </div>
          ) : done ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
              <div className="text-emerald-300 text-base font-medium">Preview complete</div>
              <button
                type="button"
                onClick={restart}
                className="px-4 py-2 text-sm rounded bg-sky-500/20 hover:bg-sky-500/30 text-sky-200 border border-sky-500/40 flex items-center gap-2"
                data-testid="anim-batch-preview-restart"
              >
                <RefreshCw className="h-4 w-4" /> Play again
              </button>
            </div>
          ) : (
            <video
              key={`${current?.clip_id}-${index}`}
              ref={videoRef}
              src={current?.video_url}
              autoPlay
              playsInline
              muted={muted}
              onEnded={onEnded}
              onError={onEnded}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid={`anim-batch-preview-video-${current?.clip_id || index}`}
            />
          )}

          {/* Per-clip overlay badges */}
          {!done && current && (
            <>
              <div className="absolute top-2 left-2 bg-black/60 backdrop-blur px-2 py-1 rounded text-[10px] font-mono uppercase tracking-wide text-amber-300">
                {index + 1}/{playlist.length} · {current.clip_id}
              </div>
              {current.isOutro && (
                <div className="absolute top-2 right-2 bg-emerald-500/80 backdrop-blur px-2 py-1 rounded text-[10px] uppercase tracking-wider text-white font-semibold">
                  Outro
                </div>
              )}
              <div className="absolute bottom-2 left-2 right-2 flex items-end justify-between gap-2 pointer-events-none">
                <div className="bg-black/60 backdrop-blur px-2 py-1 rounded text-[11px] text-zinc-200 truncate max-w-[60%]">
                  {current.label || '(no label)'}
                </div>
                <div className="bg-black/60 backdrop-blur px-2 py-1 rounded text-[10px] text-zinc-400 font-mono">
                  {current.duration_sec ? `${current.duration_sec.toFixed(1)}s` : '~10s'}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-white/10 bg-black/30 flex-wrap">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setMuted((m) => !m)}
              className="p-1.5 rounded bg-white/5 hover:bg-white/10 text-zinc-200"
              title={muted ? 'Unmute' : 'Mute'}
              data-testid="anim-batch-preview-mute"
            >
              {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
            <div className="flex items-center gap-1 text-[11px] text-zinc-400">
              <FastForward className="h-3 w-3" />
              {[1, 2, 4, 8].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRate(r)}
                  className={`px-2 py-1 rounded font-mono ${rate === r ? 'bg-amber-500/30 text-amber-100 border border-amber-500/50' : 'bg-white/5 hover:bg-white/10 text-zinc-300 border border-transparent'}`}
                  data-testid={`anim-batch-preview-rate-${r}x`}
                >
                  {r}x
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIndex((i) => Math.max(0, i - 1))}
              disabled={done || index === 0}
              className="px-2 py-1 text-xs rounded bg-white/5 hover:bg-white/10 text-zinc-200 disabled:opacity-30"
              data-testid="anim-batch-preview-prev"
            >
              ← Prev clip
            </button>
            <button
              type="button"
              onClick={() => {
                if (index < playlist.length - 1) setIndex((i) => i + 1);
                else setDone(true);
              }}
              disabled={done || playlist.length === 0}
              className="px-2 py-1 text-xs rounded bg-white/5 hover:bg-white/10 text-zinc-200 disabled:opacity-30"
              data-testid="anim-batch-preview-next"
            >
              Next clip →
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1 text-xs rounded bg-zinc-700 hover:bg-zinc-600 text-zinc-100"
              data-testid="anim-batch-preview-stop"
            >
              Stop
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SETTINGS PANEL
// ============================================================================
function SettingsField({ label, value, onChange, suffix }) {
  return (
    <label className="flex items-center justify-between gap-3 bg-white/[0.03] border border-white/10 rounded p-3">
      <span className="text-sm text-zinc-300">{label}</span>
      <span className="flex items-center gap-2">
        <input
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-24 bg-black/30 text-white text-sm px-2 py-1 rounded border border-white/10 outline-none"
        />
        <span className="text-xs text-zinc-500">{suffix}</span>
      </span>
    </label>
  );
}

function SettingsPanel() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiFetch('/admin/animation/settings');
        setS(data);
      } catch (e) { toast.error(e?.message || 'Load failed'); }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    if (!s) return;
    setSaving(true);
    try {
      await apiFetch('/admin/animation/settings', {
        method: 'PATCH',
        body: JSON.stringify({
          target_duration_sec: s.target_duration_sec,
          min_duration_sec: s.min_duration_sec,
          max_duration_sec: s.max_duration_sec,
          clip_duration_sec: s.clip_duration_sec,
          max_clip_size_mb: s.max_clip_size_mb,
        }),
      });
      toast.success('Settings saved');
    } catch (e) { toast.error(e?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-center py-8 text-zinc-500">Loading settings…</div>;
  if (!s) return null;

  return (
    <div className="space-y-2">
      <SettingsField label="Target batch length"  value={s.target_duration_sec} onChange={(v) => setS({ ...s, target_duration_sec: v })} suffix="seconds (≈3:20)" />
      <SettingsField label="Minimum batch length" value={s.min_duration_sec}    onChange={(v) => setS({ ...s, min_duration_sec: v })}    suffix="seconds (≈3:00)" />
      <SettingsField label="Maximum batch length" value={s.max_duration_sec}    onChange={(v) => setS({ ...s, max_duration_sec: v })}    suffix="seconds (≈3:30)" />
      <SettingsField label="Nominal clip length"  value={s.clip_duration_sec}   onChange={(v) => setS({ ...s, clip_duration_sec: v })}   suffix="seconds (each clip)" />
      <SettingsField label="Max upload size"      value={s.max_clip_size_mb}    onChange={(v) => setS({ ...s, max_clip_size_mb: v })}    suffix="MB per clip" />
      <div className="flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="px-3 py-1.5 text-sm rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40 flex items-center gap-1 disabled:opacity-40"
          data-testid="anim-settings-save"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save Settings
        </button>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// HoverClipPreview
// ---------------------------------------------------------------------------
// Small floating popup used by the batch builder's "Available clips" strip.
// Defaults to AUDIO ON — Chrome/Firefox allow unmuted autoplay inside a
// user-gesture handler (mouseenter counts), and admins reviewing footage
// nearly always want to hear it. If the browser overrides to muted anyway,
// we surface a speaker-off badge so the admin knows why it's silent and can
// click the popup to force-play with audio.
function HoverClipPreview({ clip, x, y, onEnter, onLeave }) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    v.volume = 1;
    const attempt = v.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(() => {
        // Browser blocked unmuted autoplay — fall back to muted so admin
        // still sees the motion, and expose a speaker-off badge so it's
        // obvious why there's no audio.
        v.muted = true;
        setMuted(true);
        v.play().catch(() => {});
      });
    }
  }, [clip.clip_id]);

  const forceUnmute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    setMuted(false);
    v.play().catch(() => {});
  };

  return (
    <div
      className="fixed z-[9998]"
      style={{ left: x, top: y, width: 320 }}
      data-testid="anim-hover-preview"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <div className="bg-zinc-950 border border-amber-500/40 rounded-lg overflow-hidden shadow-2xl">
        <div className="aspect-square bg-black relative">
          <video
            key={clip.clip_id}
            ref={videoRef}
            src={clip.video_url}
            autoPlay
            loop
            playsInline
            preload="auto"
            className="w-full h-full object-cover"
          />
          {muted && (
            <button
              type="button"
              onClick={forceUnmute}
              className="absolute top-1 right-1 bg-black/70 border border-amber-500/40 text-amber-200 rounded p-1"
              title="Browser muted the preview — click to unmute"
              data-testid="anim-hover-preview-unmute"
            >
              <VolumeX className="h-3 w-3" />
            </button>
          )}
        </div>
        <div className="px-2 py-1 text-[10px] font-mono text-amber-200 flex items-center justify-between gap-2">
          <span className="truncate">{clip.clip_id}</span>
          {clip.duration_sec ? (
            <span className="text-zinc-400 shrink-0">{clip.duration_sec.toFixed(1)}s</span>
          ) : null}
        </div>
        {clip.label && (
          <div className="px-2 pb-1 text-[10px] text-zinc-400 truncate" title={clip.label}>
            {clip.label}
          </div>
        )}
      </div>
    </div>
  );
}
